import PropTypes from 'prop-types';
import React from 'react';
import bindAll from 'lodash.bindall';
import {defineMessages, injectIntl, intlShape} from 'react-intl';
import VM from 'scratch-vm';
import CIPDcMotor01Block from '../lib/cubroid-in-pythoncode/dc-motor-01-block/dc-motor-01-block';
import CIPDcMotor02Block from '../lib/cubroid-in-pythoncode/dc-motor-02-block/dc-motor-02-block';
import CIPSoundBlock from '../lib/cubroid-in-pythoncode/sound-block/sound-block';
import CIPMasterBlock from '../lib/cubroid-in-pythoncode/master-block/master-block';
import CIPLedBlock from '../lib/cubroid-in-pythoncode/led-block/led-block';
import CIPProximityBlock from '../lib/cubroid-in-pythoncode/proximity-block/proximity-block';
import CIPLighttouchBlock from '../lib/cubroid-in-pythoncode/lighttouch-block/lighttouch-block';
import {preprocessPython, installPythonBuiltins} from '../lib/cubroid-in-pythoncode/python-compat';
import {blocksToPython} from '../lib/blocks-to-python';
import PythoncodeTabGuideComponent from '../components/pythoncode-tab/pythoncode-tab-guide.jsx';

import Box from '../components/box/box.jsx';
import styles from '../components/asset-panel/asset-panel.css';
import pcgStyles from '../components/pythoncode-tab/pythoncode-tab.css';

import errorBoundaryHOC from '../lib/error-boundary-hoc.jsx';

import {
    setIsRunPythoncode,
    updateTmpCodeText,
    updateTmpLogText,
} from '../reducers/pythoncode';

import {connect} from 'react-redux';
import Cast from 'scratch-vm/src/util/cast';

// 파이썬 탭 UI 문구. 2026-08-12 이전에는 전부 한글 하드코딩이라 **파이썬 탭만 번역 체계 밖**에
// 있었다(다른 탭은 전부 적용돼 있었다). 베트남 등 해외 사용자가 한글 버튼을 봤다.
// ⭐ defaultMessage 는 **영어**로 둔다 — 번역이 없는 로케일은 한글이 아니라 영어로 떨어진다.
//    한국어는 scratch-l10n 의 ko.json 에서 온다(다른 확장 카드와 같은 방식).
const messages = defineMessages({
    run:          {id: 'gui.pythoncodeTab.run',          defaultMessage: 'Run',        description: 'Run button in the Python tab'},
    runTitle:     {id: 'gui.pythoncodeTab.runTitle',     defaultMessage: 'Run (light and fast engine)', description: 'Tooltip for the Run button'},
    py3Title:     {id: 'gui.pythoncodeTab.py3Title',     defaultMessage: 'Real CPython 3 — libraries such as pandas and numpy work', description: 'Tooltip for the Python 3 button'},
    save:         {id: 'gui.pythoncodeTab.save',         defaultMessage: 'Save',       description: 'Save button in the Python tab'},
    saveTitle:    {id: 'gui.pythoncodeTab.saveTitle',    defaultMessage: 'Save as a .py file', description: 'Tooltip for the Save button'},
    open:         {id: 'gui.pythoncodeTab.open',         defaultMessage: 'Open',       description: 'Open button in the Python tab'},
    openTitle:    {id: 'gui.pythoncodeTab.openTitle',    defaultMessage: 'Open a .py file', description: 'Tooltip for the Open button'},
    smallerTitle: {id: 'gui.pythoncodeTab.smallerTitle', defaultMessage: 'Smaller text', description: 'Tooltip for the decrease font size button'},
    largerTitle:  {id: 'gui.pythoncodeTab.largerTitle',  defaultMessage: 'Larger text', description: 'Tooltip for the increase font size button'},
    clearCode:    {id: 'gui.pythoncodeTab.clearCode',    defaultMessage: 'Clear code', description: 'Button that deletes all code'},
    clearCodeTitle: {id: 'gui.pythoncodeTab.clearCodeTitle', defaultMessage: 'Delete all the code you wrote', description: 'Tooltip for the clear code button'},
    restart:      {id: 'gui.pythoncodeTab.restart',      defaultMessage: 'Restart',    description: 'Button that restarts the Python engine'},
    restartTitle: {id: 'gui.pythoncodeTab.restartTitle', defaultMessage: 'Restart the Python engine', description: 'Tooltip for the restart button'},
    output:       {id: 'gui.pythoncodeTab.output',       defaultMessage: 'Output',     description: 'Header of the console output area'},
    clearOutput:  {id: 'gui.pythoncodeTab.clearOutput',  defaultMessage: 'Clear',      description: 'Button that clears the console output'},
    clearOutputTitle: {id: 'gui.pythoncodeTab.clearOutputTitle', defaultMessage: 'Clear the output', description: 'Tooltip for the clear output button'},
    stop:         {id: 'gui.pythoncodeTab.stop',         defaultMessage: 'Stop',       description: 'Stop button in the Python tab'},
    stopTitle:    {id: 'gui.pythoncodeTab.stopTitle',    defaultMessage: 'Stop the running code (motors and lights turn off)', description: 'Tooltip for the Stop button'},
    stopped:      {id: 'gui.pythoncodeTab.stopped',      defaultMessage: '■ Stopped', description: 'Console message after pressing Stop'},
    // 블록 → 파이썬(2026-10-08)
    fromBlocks:   {id: 'gui.pythoncodeTab.fromBlocks',   defaultMessage: 'Blocks → Python', description: 'Button that turns the blocks into Python code'},
    fromBlocksTitle: {id: 'gui.pythoncodeTab.fromBlocksTitle', defaultMessage: 'Turn the blocks of this sprite into Python code', description: 'Tooltip for the Blocks to Python button'},
    fromBlocksConfirm: {id: 'gui.pythoncodeTab.fromBlocksConfirm', defaultMessage: 'Replace the code in the editor with the code made from blocks? (Ctrl+Z brings it back)', description: 'Confirm before replacing the code'},
    fromBlocksDone: {id: 'gui.pythoncodeTab.fromBlocksDone', defaultMessage: 'Converted {count} blocks to Python. Press ▶ Run to try it.', description: 'Console message after converting blocks'},
    fromBlocksSkipped: {id: 'gui.pythoncodeTab.fromBlocksSkipped', defaultMessage: '{count} blocks are not available in the Python tab and were left as # comments.', description: 'Console message: some blocks could not be converted'},
    fromBlocksNone: {id: 'gui.pythoncodeTab.fromBlocksNone', defaultMessage: 'No blocks to convert. Make blocks in the Code tab first.', description: 'Console message: nothing to convert'}
});
// 정지 버튼으로 실행을 끊을 때 쓰는 표시(엔진이 오류로 감싸도 찾을 수 있게 고유 문자열)
const STOP_MARK = '__CURO_PY_STOP__';

// 큐브로이드가 파이썬 탭에 제공하는 함수들. CodeMirror python 모드에 builtin 으로 넘겨
// 문법 강조를 받게 한다(아래 CodeMirror 생성부 참조).
// ⚠️ 왼쪽 함수 목록(pythoncode-tab-guide.jsx)에 함수를 추가하면 **여기에도 넣어야** 색이 붙는다.
// ⚠️ get_brigtness 는 오타처럼 보이지만 실제 API 이름이다. 고치면 기존 학생 코드가 깨진다.
const CUBROID_PY_BUILTINS = [
    'sleep',
    'turn_dcmotor1', 'turn_dcmotor1_time', 'turn_dcmotor2', 'turn_dcmotor2_time',
    'set_dcmotor1_speed', 'set_dcmotor2_speed',
    'display_show', 'display_set_pixel', 'display_clear',
    'display_alphabet', 'display_number', 'display_set', 'display_shape',
    'turnon_colorlight', 'turnon_colorlight_show', 'turnoff_colorlight',
    'play_note', 'play_note_time',
    'is_obstacle_detected', 'is_button_pressed', 'get_brigtness',
    'chart'
];
import MathUtil from 'scratch-vm/src/util/math-util';
import {ICONS} from '../lib/curo-icons.js';
import {cheer} from '../components/cheer/cheer.jsx';
// import Box from '../components/box/box.jsx';

// 파이썬 초보(중등1~2) 흔한 에러 → 친절한 한 줄 설명 (ko/en). 없는 에러는 원문만 표시.
const FRIENDLY_ERRORS = {
    NameError: {
        ko: '정의하지 않은 이름을 썼어요. 변수 철자를 확인하거나 = 로 먼저 만들었는지 보세요.',
        en: 'You used a name that is not defined. Check the spelling, or define it first with =.'
    },
    SyntaxError: {
        ko: '문법 오류예요. 괄호 ( ), 따옴표 " ", 콜론(:) 이 빠졌는지 확인하세요.',
        en: 'Syntax error. Check for a missing bracket ( ), quote " ", or colon (:).'
    },
    IndentationError: {
        ko: '들여쓰기가 안 맞아요. 같은 블록의 줄은 앞 칸을 똑같이 맞추세요 (보통 4칸).',
        en: 'Indentation is off. Lines in the same block must line up (usually 4 spaces).'
    },
    TabError: {
        ko: '탭과 띄어쓰기가 섞였어요. 들여쓰기는 띄어쓰기로 통일하세요.',
        en: 'Tabs and spaces are mixed. Use spaces for indentation.'
    },
    TypeError: {
        ko: '타입이 안 맞아요. 예: 숫자와 문자열을 그냥 더할 수 없어요. str() 나 int() 로 바꿔보세요.',
        en: 'Type mismatch. e.g. you cannot add a number and a string directly — try str() or int().'
    },
    ValueError: {
        ko: '값이 올바르지 않아요. 예: int("abc") 처럼 숫자가 아닌 걸 숫자로 바꾸려 했는지 보세요.',
        en: 'Invalid value. e.g. int("abc") — converting something that is not a number.'
    },
    ZeroDivisionError: {
        ko: '0 으로 나눌 수 없어요. 나누는 수가 0 이 아닌지 확인하세요.',
        en: 'You cannot divide by zero. Make sure the divisor is not 0.'
    },
    IndexError: {
        ko: '리스트 범위를 벗어났어요. 인덱스가 개수보다 작은지 확인하세요 (0 부터 시작).',
        en: 'List index out of range. The index must be less than the length (starts at 0).'
    },
    KeyError: {
        ko: '딕셔너리에 없는 키를 찾았어요. 키 이름(철자)을 확인하세요.',
        en: 'That key is not in the dictionary. Check the key name/spelling.'
    },
    AttributeError: {
        ko: '그 객체에 없는 기능을 불렀어요. 메서드/속성 철자를 확인하세요.',
        en: 'That object has no such method/attribute. Check the spelling.'
    },
    ModuleNotFoundError: {
        ko: '그 라이브러리를 못 찾았어요. import 이름을 확인하세요 (지금은 pandas·numpy 준비됨).',
        en: 'That library was not found. Check the import name (pandas and numpy are available).'
    },
    ImportError: {
        ko: '불러오기(import)에 실패했어요. 이름을 확인하세요.',
        en: 'Import failed. Check the name.'
    }
};

// ---- 자동완성 후보 (정적 리스트 = LLM 아님, 비용 0) ----
const PY_KEYWORDS = ['and', 'as', 'assert', 'break', 'class', 'continue', 'def', 'del', 'elif', 'else', 'except', 'False', 'finally', 'for', 'from', 'global', 'if', 'import', 'in', 'is', 'lambda', 'None', 'nonlocal', 'not', 'or', 'pass', 'raise', 'return', 'True', 'try', 'while', 'with', 'yield'];
const PY_BUILTINS = ['print', 'len', 'range', 'int', 'float', 'str', 'bool', 'list', 'dict', 'tuple', 'set', 'input', 'abs', 'min', 'max', 'sum', 'sorted', 'round', 'type', 'enumerate', 'zip', 'map', 'filter', 'open', 'format', 'isinstance', 'help', 'reversed', 'any', 'all', 'ord', 'chr'];
const PY_MODULES = ['math', 'random', 'pandas', 'numpy', 'json', 'time'];
const OUR_API = ['chart', 'pd', 'np'];
const CUBROID_FUNCS = ['turn_dcmotor1', 'turn_dcmotor2', 'turn_dcmotor1_time', 'turn_dcmotor2_time', 'set_dcmotor1_speed', 'set_dcmotor2_speed', 'play_note', 'play_note_time', 'turnon_colorlight', 'turnon_colorlight_show', 'turnoff_colorlight', 'display_show', 'display_set_pixel', 'display_clear', 'is_obstacle_detected', 'get_brigtness', 'is_button_pressed', 'sleep', 'randint'];
// 점(.) 뒤 멤버 후보 — 알려진 모듈은 그 모듈 것만, 나머지는 일반 인스턴스 메서드 (df.sqrt 같은 오답 방지)
const MODULE_MEMBERS = {
    pd: ['DataFrame', 'read_csv', 'Series', 'concat', 'to_datetime', 'merge', 'read_excel'],
    pandas: ['DataFrame', 'read_csv', 'Series', 'concat', 'to_datetime', 'merge'],
    np: ['array', 'arange', 'zeros', 'ones', 'linspace', 'mean', 'sum', 'max', 'min', 'sqrt', 'random', 'reshape', 'dot'],
    numpy: ['array', 'arange', 'zeros', 'ones', 'linspace', 'mean', 'sum'],
    math: ['sqrt', 'pi', 'floor', 'ceil', 'pow', 'factorial', 'sin', 'cos', 'tan', 'log', 'exp', 'e'],
    random: ['randint', 'choice', 'random', 'shuffle', 'sample', 'uniform']
};
const INSTANCE_MEMBERS = ['sum', 'mean', 'max', 'min', 'count', 'head', 'tail', 'describe', 'sort_values', 'idxmax', 'idxmin', 'tolist', 'value_counts', 'dropna', 'fillna', 'groupby', 'columns', 'shape', 'iloc', 'loc', 'apply', 'append', 'upper', 'lower', 'strip', 'split', 'replace', 'join', 'format', 'keys', 'values', 'items', 'get', 'pop', 'sort', 'reverse', 'index', 'startswith', 'endswith', 'copy', 'unique', 'astype', 'round'];
const PY_TOPLEVEL = [].concat(PY_KEYWORDS, PY_BUILTINS, PY_MODULES, OUR_API, CUBROID_FUNCS);

// 커스텀 파이썬 자동완성 (CodeMirror show-hint): 정적 후보 + 문서 내 단어. 비용 0.
function pythonHint (cm) {
    const CM = window.CodeMirror;
    const cur = cm.getCursor();
    const line = cm.getLine(cur.line);
    let start = cur.ch;
    while (start > 0 && (/[A-Za-z0-9_.]/).test(line.charAt(start - 1))) start--;
    const whole = line.slice(start, cur.ch);
    if (!whole) return null;
    let list = [];
    const dot = whole.lastIndexOf('.');
    if (dot >= 0) {
        // 점 뒤 멤버 완성 — 점 앞 식별자가 알려진 모듈이면 그 멤버, 아니면 일반 인스턴스 메서드
        const base = whole.slice(0, dot + 1);
        const mp = whole.slice(dot + 1).toLowerCase();
        const baseId = whole.slice(0, dot).split('.').pop();
        const pool = MODULE_MEMBERS[baseId] || INSTANCE_MEMBERS;
        list = pool.filter(m => m.toLowerCase().indexOf(mp) === 0).map(m => base + m);
    } else {
        const p = whole.toLowerCase();
        const seen = {};
        PY_TOPLEVEL.forEach(c => {
            if (c.toLowerCase().indexOf(p) === 0 && c !== whole && !seen[c]) { seen[c] = 1; list.push(c); }
        });
        // 문서 안에서 이미 쓴 이름(변수 등)도 추가
        const re = /[A-Za-z_][A-Za-z0-9_]*/g;
        const text = cm.getValue();
        let m;
        while ((m = re.exec(text)) !== null) {
            const w = m[0];
            if (w.length > 1 && w.toLowerCase().indexOf(p) === 0 && w !== whole && !seen[w]) { seen[w] = 1; list.push(w); }
        }
    }
    if (!list.length) return null;
    return {list: list, from: CM.Pos(cur.line, start), to: CM.Pos(cur.line, cur.ch)};
}

class PythoncodeTab extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleInitCodemirror',
            'runPythonCode',
            'stopPythonCode',
            'runPyodideCode',
            'savePyFile',
            'loadPyFile',
            'convertBlocksToPython',
            'insertConsole',
            'turn_dcmotor1',
            'turn_dcmotor2',
            'turn_dcmotor1_time',
            'turn_dcmotor2_time',
            'set_dcmotor1_speed',
            'set_dcmotor2_speed',
            'play_note',
            'play_note_time',
            'turnon_colorlight',
            'turnon_colorlight_show',
            'turnoff_colorlight',
            'display_set_pixel',
            'display_show',
            'display_clear',
            'is_obstacle_detected',
            'get_brigtness',
            'is_button_pressed',
            'restartPythonCode',
            'sleep',
            'randint',
            'clearLogConsole',
            'clearCodeConsole',
            'insertExampleCode',
            'increaseFont',
            'decreaseFont',

            'handleMouseDown',
            'handleMouseMove',
            'handleMouseUp'
        ]);
        
        const {
            editingTarget,
            sprites,
            stage
        } = props;
        const target = editingTarget && sprites[editingTarget] ? sprites[editingTarget] : stage;
        if (target && target.currentCostume) {
            this.state = {selectedCostumeIndex: target.currentCostume};
        } else {
            this.state = {selectedCostumeIndex: 0};
        }

        this.state = {
            cm: null,
            consoleCm: null,
            cipDcMotor01Block: new CIPDcMotor01Block(),
            cipDcMotor02Block: new CIPDcMotor02Block(),
            cipSoundBlock: new CIPSoundBlock(),
            cipMasterBlock: new CIPMasterBlock(),
            cipLedBlock: new CIPLedBlock(),
            cipProximityBlock: new CIPProximityBlock(),
            cipLighttouchBlock: new CIPLighttouchBlock(),
            isResizing: false,
            consoleHeight: 200
        };

        this.consoleContainerRef = React.createRef();
        this._pyodide = null;       // Pyodide 인스턴스 캐시(첫 실행 때 1회 로드, 이후 재사용)
        this._pyodideLoading = null; // 로드 중복 방지용 Promise
    }

    componentWillReceiveProps (nextProps) {
        const {
            editingTarget,
            sprites,
            stage
        } = nextProps;

        const target = editingTarget && sprites[editingTarget] ? sprites[editingTarget] : stage;
        if (!target) {
            return;
        }

        // If switching editing targets, reset the pythoncode index
        if (this.props.editingTarget !== editingTarget) {
            this.setState({selectedPytoncodeIndex: 0});
        } else if (this.state.selectedPythoncodeIndex > target.pythoncode.length - 1) {
            this.setState({selectedPythoncodeIndex: Math.max(target.pythoncode.length - 1, 0)});
        }
    }

    componentDidMount () {
        this.handleInitCodemirror(); // 코드미러 실행
        document.addEventListener('mousemove', this.handleMouseMove);
        document.addEventListener('mouseup', this.handleMouseUp);
    }

    componentWillUnmount () {
        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('mouseup', this.handleMouseUp);
    }

    handleInitCodemirror () {
        // python code — redux 우선, 없으면 localStorage 자동저장분 복원(새로고침 생존)
        let code = this.props.tmpCodeText ?? '';
        if (!code) {
            try { code = localStorage.getItem('curoai_python_code') || ''; } catch (e) { code = ''; }
        }
        const pythonCodeMirror = CodeMirror(document.getElementById('codemirrorBox'), {
            value: code,
            // ⭐ 큐브로이드 함수들을 파이썬 모드의 builtin 으로 등록해 색이 입혀지게 한다.
            // 이게 없으면 turn_dcmotor1·sleep·display_show 같은 **학생이 제일 봐야 할 명령어가
            // 전부 무채색**이 되고, def 와 문자열만 색이 있어서 코드가 회색 덩어리로 보인다.
            // (CodeMirror python 모드가 parserConf.extra_builtins 를 지원한다 — mode/python/python.js)
            // 함수를 추가하면 여기에도 이름을 넣어야 색이 붙는다.
            mode: {name: 'python', extra_builtins: CUBROID_PY_BUILTINS},
            theme: 'darcula',
            lineNumbers: true,
            indentUnit: 4,
            styleActiveLine: true,
            gutters: ['error'],
            matchBrackets: true,
            lineWrapping: true,
            extraKeys: {'Ctrl-Space': cm => cm.showHint({hint: pythonHint, completeSingle: false})}
        });
        pythonCodeMirror.setSize('100%', '50');

		pythonCodeMirror.on('change', cm => {
            const nowValue = cm.getValue();
            this.props.dispatchUpdateTmpCodeText(nowValue);
            try { localStorage.setItem('curoai_python_code', nowValue); } catch (e) {} // 자동저장(새로고침 생존)
		});

        // 타이핑 중 자동완성 팝업 (단어 2글자 이상일 때). LLM 아님 = 비용 0.
        pythonCodeMirror.on('inputRead', (cm, change) => {
            const t = change.text && change.text[0];
            if (!t || !(/[A-Za-z_.]/).test(t)) return;
            const cur = cm.getCursor();
            const l = cm.getLine(cur.line);
            let s = cur.ch;
            while (s > 0 && (/[A-Za-z0-9_.]/).test(l.charAt(s - 1))) s--;
            if (cur.ch - s < 2) return; // 2글자부터
            cm.showHint({hint: pythonHint, completeSingle: false});
        });

        // console
        const logCode = this.props.tmpLogText ?? '';
        const consoleCodeMirror = CodeMirror(document.getElementById('consoleBox'), {
            value: logCode,
            mode: '',
            theme: 'darcula',
            lineNumbers: false,
            indentUnit: 4,
            styleActiveLine: true,
            gutters: ['error'],
            readOnly: true,
            matchBrackets: true,
            lineWrapping: true,
        });
    
        // 초기 높이 설정
        consoleCodeMirror.setSize('100%', this.state.consoleHeight);

		consoleCodeMirror.on('change', cm => {
            const nowValue = cm.getValue();
            this.props.dispatchUpdateTmpLogText(nowValue);
		});
        consoleCodeMirror.execCommand('goDocEnd'); // 스크롤 가장 아래로 이동

        this.setState({
            cm: pythonCodeMirror,
            consoleCm: consoleCodeMirror,
        });

        // 폰트 크기: localStorage 저장값 복원 후 두 에디터에 적용 (교실 프로젝터·시력 대응, quick win)
        try { this.fontSize = parseInt(localStorage.getItem('curoai_python_fontsize'), 10) || 14; } catch (e) { this.fontSize = 14; }
        this._setEditorFont(this.fontSize, [pythonCodeMirror, consoleCodeMirror]);
    }

    // ── 에디터 폰트 크기 조절 (코드+콘솔 동시, 10~30px, localStorage 저장) ──
    _setEditorFont (size, editors) {
        editors.forEach(cm => {
            if (!cm) return;
            cm.getWrapperElement().style.fontSize = `${size}px`;
            cm.refresh();
        });
    }

    _persistAndApplyFont () {
        try { localStorage.setItem('curoai_python_fontsize', String(this.fontSize)); } catch (e) {}
        this._setEditorFont(this.fontSize, [this.state.cm, this.state.consoleCm]);
    }

    increaseFont () {
        this.fontSize = Math.min(30, (this.fontSize || 14) + 2);
        this._persistAndApplyFont();
    }

    decreaseFont () {
        this.fontSize = Math.max(10, (this.fontSize || 14) - 2);
        this._persistAndApplyFont();
    }

    // 2. 새로운 메서드 3개 추가
    handleMouseDown (e) {
        this.setState({isResizing: true});
        e.preventDefault();
    }

    handleMouseMove (e) {
        if (!this.state.isResizing) return;

        const container = this.consoleContainerRef.current;
        if (!container) return;

        const containerRect = container.parentElement.getBoundingClientRect();
        const newHeight = containerRect.bottom - e.clientY;

        // 최소/최대 높이 제한
        const minHeight = 50;
        const maxHeight = containerRect.height - 150; // 상단 코드 영역 최소 공간 확보

        if (newHeight >= minHeight && newHeight <= maxHeight) {
            this.setState({consoleHeight: newHeight}, () => {
                // CodeMirror 높이도 업데이트
                if (this.state.consoleCm) {
                    this.state.consoleCm.setSize('100%', newHeight);
                }
            });
        }
    }

    handleMouseUp () {
        this.setState({isResizing: false});
    }

    async runPythonCode () {
        try {
            this._stopReq = false; // [정지] 표시 초기화
            const jsPython = window.jspython.jsPython;
            // 2026-09-07: 열0 주석·`+=`·`pass` 전처리 (python-compat.js). 줄 수 보존 → 오류 줄번호 유지.
            const cValue = preprocessPython(this.state.cm?.getValue());
            if (cValue) {
                this.props.dispatchSetIsRunPythoncode(true);

                const interpreter = await jsPython();
                // [정지]: 모든 명령(내장 함수·모터·sleep…)이 불리기 전에 정지 표시를 본다 → 다음 명령에서 실행이 끊긴다
                //   (2026-10-06 직원 의견 "실행 버튼만 있고 정지 버튼 없음"). 명령을 하나도 안 부르는 무한 계산만은 못 끊는다
                const addFn = interpreter.addFunction.bind(interpreter);
                interpreter.addFunction = (name, fn) => addFn(name, async (...args) => {
                    if (this._stopReq) throw new Error(STOP_MARK);
                    return fn(...args);
                });
                interpreter.assignGlobalContext({
                    True: true,
                    False: false
                });
                // 2026-09-07: len/str/int 등 내장 + .upper/.append 등 메서드 보정 (python-compat.js).
                //   원 엔진엔 이게 없어 교안의 기초 문법이 전부 오류였다 (실측 46개 문법 중 내장함수 전부 실패).
                installPythonBuiltins(interpreter);

                interpreter.addFunction('print', async (...args) => {
                    const argsJoin = args.join(' ');
                    this.insertConsole(argsJoin);
                    return;
                });
                interpreter.addFunction('turn_dcmotor1', async (arg) => {
                        // return `run turn_dcmotor1("${arg}")`;
                        return await this.turn_dcmotor1(arg);
                    });
                interpreter.addFunction('turn_dcmotor2', async (arg) => {
                        return await this.turn_dcmotor2(arg);
                    });
                interpreter.addFunction('turn_dcmotor1_time', async (arg, second) => {
                        return await this.turn_dcmotor1_time(arg, second);
                    });
                interpreter.addFunction('set_dcmotor1_speed', async (value) => {
                        return await this.set_dcmotor1_speed(value);
                    });
                interpreter.addFunction('set_dcmotor2_speed', async (value) => {
                        return await this.set_dcmotor2_speed(value);
                    });
                interpreter.addFunction('turn_dcmotor2_time', async (arg, second) => {
                        return await this.turn_dcmotor2_time(arg, second);
                    });
                interpreter.addFunction('play_note', async (param) => {
                        return await this.play_note(param);
                    });
                interpreter.addFunction('play_note_time', async (param, time) => {
                        return await this.play_note_time(param, time);
                    });
                interpreter.addFunction('turnon_colorlight', async (color) => {
                    return await this.turnon_colorlight(color);
                });
                interpreter.addFunction('turnon_colorlight_show', async (key) => {
                    return await this.turnon_colorlight_show(key);
                });
                interpreter.addFunction('turnoff_colorlight', async () => {
                    return await this.turnoff_colorlight();
                });
                interpreter.addFunction('display_set_pixel', async (matrixHex) => {
                    return await this.display_set_pixel(matrixHex);
                });
                interpreter.addFunction('display_show', async (key) => {
                    return await this.display_show(key);
                });
                interpreter.addFunction('display_clear', async () => {
                    return await this.display_clear();
                });
                interpreter.addFunction('is_obstacle_detected', async () => {
                    return this.is_obstacle_detected();
                });
                interpreter.addFunction('get_brigtness', async () => {
                    return this.get_brigtness();
                });
                interpreter.addFunction('is_button_pressed', async () => {
                    return this.is_button_pressed();
                });
                interpreter.addFunction('sleep', async (sec) => {
                    await this.sleep(sec)
                    return;
                });
                interpreter.addFunction('randint', async (min, max) => {
                    const result = this.randint(min, max);
                    // this.insertConsole(result);
                    return result;
                });

                // interpreter.addFunction('mot02ReverseTurn', () => {
                //         console.log('mot02ReverseTurn!')
                //         return 'mot02ReverseTurn!';
                //     });

                interpreter.evaluate(cValue)
                    .then(
                        result => {
                            console.log('Result => ', result);
                            if (typeof result === 'object') {
                                this.insertConsole(JSON.stringify(result));
                            } else if (result !== undefined) {
                                this.insertConsole(result);
                            }

                            this.props.dispatchSetIsRunPythoncode(false);
                        },
                        err => {
                            console.log('err', err);
                            // [정지]로 끊은 것은 오류가 아니다
                            if (this._stopReq || String(err && err.message).indexOf(STOP_MARK) >= 0) this.insertConsole(this.props.intl.formatMessage(messages.stopped));
                            else this.insertConsole(err?.message);
                            this._stopReq = false;
                            this.props.dispatchSetIsRunPythoncode(false);
                        }
                    );
            }
        } catch (error) {
            console.log(error);
        }
    }

    // 현재 로케일 (친절한 에러 설명용: 한국어면 ko, 그 외 en)
    _locale () {
        const l = this.props.intl && this.props.intl.locale ? this.props.intl.locale : 'en';
        return (l && l.indexOf('ko') === 0) ? 'ko' : 'en';
    }

    // 파이썬 traceback 문자열에서 에러 종류·설명·줄번호 뽑기
    _parseError (rawMessage) {
        const s = String(rawMessage).replace(/\r/g, '');
        const lines = s.split('\n');
        // 마지막 비어있지 않은 줄 = "Type: detail"
        let last = '';
        for (let i = lines.length - 1; i >= 0; i--) {
            if (lines[i].trim()) { last = lines[i].trim(); break; }
        }
        const m = last.match(/^([A-Za-z_][A-Za-z0-9_.]*(?:Error|Exception|Warning|Interrupt|Exit)):?\s*(.*)$/);
        const type = m ? m[1].split('.').pop() : null; // builtins.NameError → NameError
        const detail = m ? m[2] : last;
        // 줄번호: <exec>(사용자 코드) 프레임 우선, 없으면 아무 line
        let lineNo = null;
        let mm;
        const reExec = /File "<exec>",? line (\d+)/g;
        while ((mm = reExec.exec(s)) !== null) lineNo = parseInt(mm[1], 10);
        if (lineNo === null) {
            const reAny = /line (\d+)/g;
            while ((mm = reAny.exec(s)) !== null) lineNo = parseInt(mm[1], 10);
        }
        return {type: type, detail: detail, lineNo: lineNo};
    }

    // 에러 난 줄을 코드 에디터에서 빨갛게 표시
    _markErrorLine (lineNo) {
        const cm = this.state.cm;
        if (!cm) return;
        const line = lineNo - 1;
        if (line >= 0 && line < cm.lineCount()) {
            cm.addLineClass(line, 'background', 'cm-error-line');
            if (!this._errorLines) this._errorLines = [];
            this._errorLines.push(line);
        }
    }
    _clearErrorMarks () {
        const cm = this.state.cm;
        if (!cm || !this._errorLines) return;
        this._errorLines.forEach(l => cm.removeLineClass(l, 'background', 'cm-error-line'));
        this._errorLines = [];
    }

    // 콘솔에 친절한 에러 출력 + 줄 하이라이트
    _showFriendlyError (rawMessage) {
        const info = this._parseError(rawMessage);
        if (info.lineNo) this._markErrorLine(info.lineNo);
        let head = `❌ ${info.type || 'Error'}`;
        if (info.lineNo) head += ` (line ${info.lineNo})`;
        if (info.detail) head += `: ${info.detail}`;
        this.insertConsole(head);
        const fr = FRIENDLY_ERRORS[info.type];
        if (fr) this.insertConsole(`💡 ${fr[this._locale()] || fr.en}`);
    }

    // 파이썬 → 데이터 차트 브리지: 파이썬에서 chart(labels, values) 호출 시 우리 차트 확장(cubroidchart)에 그림.
    // scratch-vm 변경 없이 runtime._primitives(getOpcodeFunction)로 차트 블록 함수를 직접 호출.
    _setupChartBridge (py) {
        py.globals.set('_cubroid_chart', (labelsJson, valuesJson, kind) => {
            try {
                const rt = this.props.vm && this.props.vm.runtime;
                if (!rt || !rt.getOpcodeFunction) return 'no_runtime';
                const addFn = rt.getOpcodeFunction('cubroidchart_addData');
                const showFn = rt.getOpcodeFunction('cubroidchart_showChart');
                if (!addFn || !showFn) return 'no_extension'; // 데이터 차트 확장 미추가
                const clearFn = rt.getOpcodeFunction('cubroidchart_clearData');
                if (clearFn) clearFn({});
                const labels = JSON.parse(labelsJson);
                const values = JSON.parse(valuesJson);
                const n = Math.min(labels.length, values.length);
                for (let i = 0; i < n; i++) {
                    addFn({LABEL: String(labels[i]), VALUE: Number(values[i])});
                }
                showFn({TYPE: kind || 'bar'});
                return 'ok';
            } catch (e) {
                return `error:${e && e.message ? e.message : String(e)}`;
            }
        });
        // 파이썬 쪽 래퍼: 리스트/pandas Series를 받아 문자열/실수로 변환 후 브리지 호출
        py.runPython([
            'import json as _json',
            'def chart(labels, values, kind="bar"):',
            '    """Draw labels/values (list or pandas Series) with the Data Chart. kind=bar/line/pie"""',
            '    _st = _cubroid_chart(_json.dumps([str(x) for x in labels]), _json.dumps([float(x) for x in values]), kind)',
            '    if _st == "no_extension":',
            '        print("Please add the Data Chart extension first (extension button at the bottom-left of the palette).")',
            '    elif isinstance(_st, str) and _st.startswith("error"):',
            '        print("Chart error:", _st)',
            ''
        ].join('\n'));
    }

    // Pyodide(진짜 CPython 3.14)로 실행 — jspython과 별개 경로. 첫 실행 때 런타임(약 9MB wasm)을 1회 지연 로드.
    // print/에러는 콘솔로. import된 패키지는 자동 로드. chart(labels,values)로 데이터 차트 연동.
    async runPyodideCode () {
        const cValue = this.state.cm?.getValue();
        if (!cValue) return;
        this._clearErrorMarks(); // 이전 실행의 에러 줄 표시 지우기
        try {
            this.props.dispatchSetIsRunPythoncode(true);

            // 최초 1회 Pyodide 로드 (중복 클릭 대비 _pyodideLoading Promise 공유)
            if (!this._pyodide) {
                if (!this._pyodideLoading) {
                    if (!window.loadPyodide) {
                        throw new Error('Pyodide loader not found (static/pyodide/pyodide.js).');
                    }
                    this.insertConsole('⏳ Loading Python 3… (first time only, please wait)');
                    const indexURL = new URL('static/pyodide/', document.baseURI).href;
                    this._pyodideLoading = window.loadPyodide({indexURL: indexURL}).then(py => {
                        py.setStdout({batched: s => this.insertConsole(s)});
                        py.setStderr({batched: s => this.insertConsole(s)});
                        this._setupChartBridge(py); // 파이썬 → 데이터 차트 연동
                        this._pyodide = py;
                        this.insertConsole('✅ Python 3 ready');
                        return py;
                    });
                }
                await this._pyodideLoading;
                this._pyodideLoading = null;
            }

            // import된 패키지(pandas/numpy 등) 자동 로드 (self-host wheel에서)
            if ((/^\s*(import|from)\s/m).test(cValue)) {
                this.insertConsole('📦 Loading libraries… (pandas etc. — a few seconds the first time)');
            }
            try {
                await this._pyodide.loadPackagesFromImports(cValue);
            } catch (pkgErr) {
                this.insertConsole(`(package load warning: ${pkgErr && pkgErr.message ? pkgErr.message : pkgErr})`);
            }

            // 코드 실행 (async — await/입출력 지원)
            try {
                const result = await this._pyodide.runPythonAsync(cValue);
                if (result !== undefined && result !== null) {
                    this.insertConsole(Cast.toString(result));
                }
                cheer('pyOk'); // 에러 없이 끝났을 때만 축하(처음 1회) — catch로 안 빠진 경로
            } catch (err) {
                this._showFriendlyError(err && err.message ? err.message : String(err));
            }
        } catch (e) {
            this.insertConsole('Failed to load Python 3: ' + (e && e.message ? e.message : String(e)));
        } finally {
            this.props.dispatchSetIsRunPythoncode(false);
        }
    }

    // console box에 내용 추가
    insertConsole (data) {
        const doc = this.state.consoleCm?.getDoc();
        // var cursor = doc.getCursor(); // gets the line number in the cursor position
        // const line = doc.getLine(cursor.line); // get the line contents
        const lastLine = doc?.lastLine();
        const line = doc.getLine(lastLine); // get the line contents
        // console.log(lastLine);
        var pos = {
            line: lastLine
        };
        const strData = Cast.toString(data);
        if (line.length === 0) {
            // check if the line is empty
            // add the data
            doc.replaceRange(strData, pos);
        } else {
            // add a new line and the data
            doc.replaceRange("\n" + strData, pos);
        }

        // ### 로그 1,000줄 까지만 보여주기 [Begin] ###
        const newLastLine2 = doc?.lastLine();
        // const firstLing2 = doc.firstLine();
        const maxLine = 1000;
        if (lastLine > maxLine) {
            const cutContent = doc.getRange({line: newLastLine2 - maxLine}, {line: newLastLine2});
            doc.setValue(cutContent);
        }
        // ### 로그 1,000줄 까지만 보여주기 [End] ###

        this.state.consoleCm?.execCommand('goDocEnd'); // 스크롤 가장 아래로 이동
    }
    clearLogConsole () {
        const doc = this.state.consoleCm?.getDoc();
        doc.setValue('');

        // const lastLine = doc?.lastLine();
        // console.log(lastLine);
        
        // var pos = {
        //     line: lastLine
        // };
        // doc.removeLineClass(pos);
        // this.state.consoleCm?.execCommand('deleteLineCtrl'); // 스크롤 가장 아래로 이동
    }
    clearCodeConsole () {
        const doc = this.state.cm?.getDoc();
        doc.setValue('');

        // const lastLine = doc?.lastLine();
        // console.log(lastLine);
        
        // var pos = {
        //     line: lastLine
        // };
        // doc.removeLineClass(pos);
        // this.state.consoleCm?.execCommand('deleteLineCtrl'); // 스크롤 가장 아래로 이동
    }

    // 새로고침
    restartPythonCode () {
        try {
            // this.props.dispatchUpdateTmpCodeText('');
            // this.props.dispatchUpdateTmpLogText('');
            window.location.reload();
        } catch (error) {
            console.log(error);
        }
    }

    // 💾 코드를 .py 파일로 저장(다운로드)
    savePyFile () {
        try {
            const code = this.state.cm ? this.state.cm.getValue() : '';
            const blob = new Blob([code], {type: 'text/x-python;charset=utf-8'});
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'main.py';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (error) {
            console.log(error);
        }
    }

    // 📂 .py 파일 열기(업로드) → 에디터에 불러오기
    loadPyFile () {
        try {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.py,.txt,text/x-python,text/plain';
            input.onchange = e => {
                const file = e.target.files && e.target.files[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = ev => {
                    const text = String(ev.target.result);
                    if (this.state.cm) this.state.cm.setValue(text);
                };
                reader.readAsText(file);
            };
            input.click();
        } catch (error) {
            console.log(error);
        }
    }

    // 블록 → 파이썬(2026-10-08, 직원 질문 "블록코딩을 파이썬으로 바꾸는 기능 있나요?")
    // 지금 스프라이트의 블록을 파이썬 탭에서 그대로 실행되는 코드로 바꿔 에디터에 넣는다. 변환 규칙·시험은 lib/blocks-to-python.js.
    // 에디터에 직접 쓴 코드가 있으면 한 번 묻는다(예제처럼 Ctrl+Z 로도 되돌아간다). 방금 바꾼 코드 그대로면 묻지 않는다.
    convertBlocksToPython () {
        try {
            const f = (m, v) => this.props.intl.formatMessage(m, v);
            const target = this.props.vm && this.props.vm.editingTarget;
            const cm = this.state.cm;
            if (!target || !cm) return;
            const res = blocksToPython(target, this._locale());
            if (!res.converted) {
                this.insertConsole(f(messages.fromBlocksNone));
                return;
            }
            const now = cm.getValue();
            if (now.trim() && now !== this._lastFromBlocks && !window.confirm(f(messages.fromBlocksConfirm))) return;
            cm.setValue(res.code);
            this._lastFromBlocks = cm.getValue();
            cm.focus();
            this.insertConsole(f(messages.fromBlocksDone, {count: res.converted}));
            if (res.skipped) this.insertConsole(f(messages.fromBlocksSkipped, {count: res.skipped}));
        } catch (error) {
            console.log(error);
            this.insertConsole(String(error && error.message ? error.message : error));
        }
    }

    // code 삽입
    insertExampleCode (exampleCode) {
        try {
            const doc = this.state.cm?.getDoc();
            // 예제는 문자열이거나 {en, ko} 객체다. 객체면 현재 언어로 고른다.
            // ⭐ 여기서 고르는 이유 = 가이드 컴포넌트를 안 건드리기 위해. 로케일을 아는 건 이쪽뿐이다.
            // 12개어 예제를 다 만들 이유는 없다 — **코드는 언어 무관이고 주석만 갈리므로 en/ko 2벌**.
            // ko 가 아닌 로케일은 전부 en 으로 떨어진다(확장 카드 설명과 같은 방침).
            const locale = (this.props.intl && this.props.intl.locale) || 'en';
            const picked = (exampleCode && typeof exampleCode === 'object') ?
                (exampleCode[locale] || exampleCode.en) : exampleCode;
            const strData = Cast.toString(picked);
            // 예제는 **끼워넣지 않고 갈아끼운다.**
            // 전에는 커서 위치에 replaceRange 로 넣어서, 예제를 두 번 누르면 아래로 계속
            // 쌓였다 — 학생이 매번 지워야 했다. 예제는 "이걸 보고 배우는 것"이라
            // 갈아끼우는 게 맞다. 실수로 눌러도 **Ctrl+Z 로 되돌아간다**(CodeMirror 되돌리기).
            doc.setValue(strData.replace(/^\n/, ''));
            this.state.cm.focus();
            doc.setCursor(doc.lineCount(), 0);
        } catch (error) {
            console.log(error);
        }
    }

    async sleep (sec) {
        sec = Cast.toNumber(sec);
        let millionSeconds = Cast.toNumber(sec) * 1000;
        millionSeconds = MathUtil.clamp(millionSeconds, 0, 360000);
        this.insertConsole(`sleep(${sec})`);
        
        return new Promise((resolve, reject) => {
            const until = Date.now() + millionSeconds;
            const tick = () => {
                if (this._stopReq) { reject(new Error(STOP_MARK)); return; }
                const left = until - Date.now();
                if (left <= 0) { resolve(`sleep(${sec})`); return; }
                setTimeout(tick, Math.min(100, left));
            };
            tick();
        });
    }

    // [정지] — 실행 중인 코드를 다음 명령에서 끊고, 로봇 모터·불빛을 끈다
    async stopPythonCode () {
        if (!this.props.isRunPythoncode) return;
        this._stopReq = true;
        // 로봇이 계속 달리지 않게(연결 안 된 블록은 각 함수가 알아서 무시) — 정지 표시가 켜져 있으면 래퍼가 막으므로 직접 부른다
        // 연결된 블록만 끈다(안 그러면 '연결되지 않았습니다' 오류가 정지 때마다 찍힌다)
        const on = id => { try { return this.props.vm.getPeripheralIsConnected(id); } catch (e) { return false; } };
        try { if (on('cubroiddcmotor01')) await this.turn_dcmotor1('STOP'); } catch (e) { /* 끊김 */ }
        try { if (on('cubroiddcmotor02')) await this.turn_dcmotor2('STOP'); } catch (e) { /* 끊김 */ }
        try { if (on('cubroidmaster')) await this.turnoff_colorlight(); } catch (e) { /* 끊김 */ }
    }

    randint (min, max) {
        if (!min) {
            min = 0;
        }
        if (!max) {
            max = 0;
        }
        return Math.floor(Math.random()*(max-min+1)) + min;
    }

    // motor block [Begin]
    // 모터 속도 (0~255 하드웨어 값 그대로 — 블록의 %와 달리 파이썬은 심화라 정직하게 노출).
    // peripheral 의 setMotorSpeed 가 clamp 하고, 블록과 같은 상태를 공유한다(로봇이 하나니까).
    // BLE 전송은 없어서 연결 전에도 정할 수 있다 — 그래서 "not connected" 에러를 내지 않는다.
    async set_dcmotor1_speed (value) {
        this.insertConsole(`run set_dcmotor1_speed(${Cast.toNumber(value)});`);
        const p = this.props.vm.runtime.peripheralExtensions?.cubroiddcmotor01;
        if (p && p.setMotorSpeed) p.setMotorSpeed(Cast.toNumber(value));
        else this.insertConsole('[Error] Add the DC Motor 1 extension first.');
    }

    async set_dcmotor2_speed (value) {
        this.insertConsole(`run set_dcmotor2_speed(${Cast.toNumber(value)});`);
        const p = this.props.vm.runtime.peripheralExtensions?.cubroiddcmotor02;
        if (p && p.setMotorSpeed) p.setMotorSpeed(Cast.toNumber(value));
        else this.insertConsole('[Error] Add the DC Motor 2 extension first.');
    }

    async turn_dcmotor1 (arg) {
        const vmCubroidDcMotor01 = this.props.vm.runtime.peripheralExtensions?.cubroiddcmotor01;
        this.insertConsole(`run turn_dcmotor1("${arg}");`);

        const result = await this.state.cipDcMotor01Block.dcMotorControl(vmCubroidDcMotor01, arg);

        if (result?.error) {
            this.insertConsole(result?.error);
        }
    }

    async turn_dcmotor2 (arg) {
        const vmCubroidDcMotor02 = this.props.vm.runtime.peripheralExtensions?.cubroiddcmotor02;
        this.insertConsole(`run turn_dcmotor2("${arg}");`);

        const result = await this.state.cipDcMotor02Block.dcMotorControl(vmCubroidDcMotor02, arg);

        if (result?.error) {
            this.insertConsole(result?.error);
        }
    }

    async turn_dcmotor1_time (arg, second) {
        const vmCubroidDcMotor01 = this.props.vm.runtime.peripheralExtensions?.cubroiddcmotor01;
        this.insertConsole(`run turn_dcmotor1_time("${arg}", ${second});`);

        const result = await this.state.cipDcMotor01Block.dcMotorWithTimeControl(vmCubroidDcMotor01, arg, second);

        if (result?.error) {
            this.insertConsole(result?.error);
        }
    }

    async turn_dcmotor2_time (arg, second) {
        const vmCubroidDcMotor02 = this.props.vm.runtime.peripheralExtensions?.cubroiddcmotor02;
        this.insertConsole(`run turn_dcmotor2_time("${arg}", ${second});`);

        const result = await this.state.cipDcMotor02Block.dcMotorWithTimeControl(vmCubroidDcMotor02, arg, second);

        if (result?.error) {
            this.insertConsole(result?.error);
        }
    }
    // motor block [End]

    // 사운드블록 [Begin]
    async play_note (param) {
        const vmCubroidSound = this.props.vm.runtime.peripheralExtensions?.cubroidsound;
        this.insertConsole(`run play_note("${param}");`);
    
        const result = await this.state.cipSoundBlock.soundControl(vmCubroidSound, param);

        if (result?.error) {
            this.insertConsole(result?.error);
        }
    }

    async play_note_time (param, time) {
        const vmCubroidSound = this.props.vm.runtime.peripheralExtensions?.cubroidsound;
        this.insertConsole(`run play_note_time("${param}", ${time});`);
    
        const result = await this.state.cipSoundBlock.soundBeatControl(vmCubroidSound, param, time);

        if (result?.error) {
            this.insertConsole(result?.error);
        }
    }
    // 사운드블록 [End]

    // 마스터블록 [Begin]
    async turnon_colorlight (color) {
        const vmCubroidMaster = this.props.vm.runtime.peripheralExtensions?.cubroidmaster;
        this.insertConsole(`run turnon_colorlight("${color}")`);
    
        const result = await this.state.cipMasterBlock.masterColorControl(vmCubroidMaster, color);

        if (result?.error) {
            this.insertConsole(result?.error);
        }
    }
    async turnon_colorlight_show (key) {
        const vmCubroidMaster = this.props.vm.runtime.peripheralExtensions?.cubroidmaster;
        this.insertConsole(`run turnon_colorlight_show("${key}")`);
    
        const result = await this.state.cipMasterBlock.masterColor2(vmCubroidMaster, key);

        if (result?.error) {
            this.insertConsole(result?.error);
        }
    }
    async turnoff_colorlight () {
        const vmCubroidMaster = this.props.vm.runtime.peripheralExtensions?.cubroidmaster;
        this.insertConsole(`run turnoff_colorlight()`);
    
        const result = await this.state.cipMasterBlock.masterColorControl(vmCubroidMaster, '#000000');

        if (result?.error) {
            this.insertConsole(result?.error);
        }
    }
    // 마스터블록 [End]

    // LED블록 [Begin]
    async display_set_pixel (matrixHex) {
        const vmLedMaster = this.props.vm.runtime.peripheralExtensions?.cubroidled;
        this.insertConsole(`run display_set_pixel("${matrixHex}")`);
    
        const result = await this.state.cipLedBlock.ledControl(vmLedMaster, matrixHex);

        if (result?.error) {
            this.insertConsole(result?.error);
        }
    }
    async display_show (key) {
        const vmLedMaster = this.props.vm.runtime.peripheralExtensions?.cubroidled;
        this.insertConsole(`run display_show("${key}")`);
    
        const result = await this.state.cipLedBlock.ledDisplay(vmLedMaster, key);

        if (result?.error) {
            this.insertConsole(result?.error);
        }
    }
    async display_clear () {
        const vmLedMaster = this.props.vm.runtime.peripheralExtensions?.cubroidled;
        this.insertConsole('run display_clear()');
    
        const result = await this.state.cipLedBlock.ledDisplay(vmLedMaster, 'CLEAR');

        if (result?.error) {
            this.insertConsole(result?.error);
        }
    }
    // LED블록 [End]

    // Proximity [Begin]
    is_obstacle_detected () {
        const vmLedProximity = this.props.vm.runtime.peripheralExtensions?.cubroidproximity;
        this.insertConsole(`run is_obstacle_detected();`);
    
        const result = this.state.cipProximityBlock.isProximity(vmLedProximity);

        if (result?.error) {
            this.insertConsole(result?.error);
            return false;
        }
        return result;
    }
    // Proximity [End]

    // light / button [Begin]
    get_brigtness () {
        const vmLedLightTouch = this.props.vm.runtime.peripheralExtensions?.cubroidlighttouch;
        this.insertConsole(`run get_brigtness();`);
    
        const result = this.state.cipLighttouchBlock.getLightValue(vmLedLightTouch);

        if (result?.error) {
            this.insertConsole(result?.error);
            return false;
        }
        return result;
    }
    is_button_pressed () {
        const vmLedLightTouch = this.props.vm.runtime.peripheralExtensions?.cubroidlighttouch;
        this.insertConsole(`run is_button_pressed();`);
    
        const result = this.state.cipLighttouchBlock.isButtonPress(vmLedLightTouch);

        if (result?.error) {
            this.insertConsole(result?.error);
            return false;
        }
        return result;
    }
    // light / button [End]

    render () {
        const {consoleHeight} = this.state;

        const {
            vm,
            isRunPythoncode,
            tmpCodeText,
        } = this.props;

        // 번역 헬퍼. 아래 JSX 에서 f(messages.run) 처럼 쓴다.
        const f = m => this.props.intl.formatMessage(m);

        if (!vm.editingTarget) {
            return null;
        }

        return (
            <Box className={styles.wrapper}>
                <PythoncodeTabGuideComponent insertExampleCode={this.insertExampleCode} />

                <div
                    style={{
                        display: 'flex',
                        flex: 1,
                        alignItems: 'stretch',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flex: 1,
                            minHeight: 50,
                            overflow: 'hidden'
                        }}
                    >
                        <div id="codemirrorBox" style={{flex: 1, overflow: 'hidden'}}></div>
                    </div>
                    {/* 툴바 — 실행(주인공) / 파일 / 보기·정리 세 묶음. 에디터·콘솔과 같은 톤이라 한 덩어리로 읽힌다 */}
                    <div className={pcgStyles.pcgToolbar}>
                        <div className={pcgStyles.pcgGroup}>
                            <button
                                onClick={this.runPythonCode}
                                disabled={isRunPythoncode}
                                className={`${pcgStyles.pcgRunBtn} ${pcgStyles.primary}`}
                                title={f(messages.runTitle)}
                            >{`▶ ${f(messages.run)}`}</button>
                            <button
                                onClick={this.stopPythonCode}
                                disabled={!isRunPythoncode}
                                className={`${pcgStyles.pcgRunBtn} ${pcgStyles.danger}`}
                                title={f(messages.stopTitle)}
                            >{`■ ${f(messages.stop)}`}</button>
                            <button
                                onClick={this.runPyodideCode}
                                disabled={isRunPythoncode}
                                className={`${pcgStyles.pcgRunBtn} ${pcgStyles.py3}`}
                                title={f(messages.py3Title)}
                            >
                                <span style={{display: 'inline-flex'}} dangerouslySetInnerHTML={{__html: ICONS.code}} />
                                {'Python 3'}
                            </button>
                            <span className={pcgStyles.pcgDivider} />
                            <button onClick={this.savePyFile} className={pcgStyles.pcgRunBtn} title={f(messages.saveTitle)}>
                                <span style={{display: 'inline-flex'}} dangerouslySetInnerHTML={{__html: ICONS.disk}} />
                                {f(messages.save)}
                            </button>
                            <button onClick={this.loadPyFile} className={pcgStyles.pcgRunBtn} title={f(messages.openTitle)}>
                                <span style={{display: 'inline-flex'}} dangerouslySetInnerHTML={{__html: ICONS.folder}} />
                                {f(messages.open)}
                            </button>
                            <span className={pcgStyles.pcgDivider} />
                            <button onClick={this.convertBlocksToPython} disabled={isRunPythoncode} className={`${pcgStyles.pcgRunBtn} ${pcgStyles.fromBlocks}`} title={f(messages.fromBlocksTitle)}>
                                {f(messages.fromBlocks)}
                            </button>
                        </div>
                        <div className={pcgStyles.pcgGroup}>
                            <button onClick={this.decreaseFont} className={`${pcgStyles.pcgRunBtn} ${pcgStyles.pcgIconBtn}`} title={f(messages.smallerTitle)}>{'A−'}</button>
                            <button onClick={this.increaseFont} className={`${pcgStyles.pcgRunBtn} ${pcgStyles.pcgIconBtn}`} title={f(messages.largerTitle)}>{'A+'}</button>
                            <span className={pcgStyles.pcgDivider} />
                            <button onClick={this.clearCodeConsole} className={`${pcgStyles.pcgRunBtn} ${pcgStyles.danger}`} title={f(messages.clearCodeTitle)}>{f(messages.clearCode)}</button>
                            <button onClick={this.restartPythonCode} className={`${pcgStyles.pcgRunBtn} ${pcgStyles.danger}`} title={f(messages.restartTitle)}>{f(messages.restart)}</button>
                        </div>
                    </div>

                    {/* 드래그 핸들 추가 */}
                    <div
                        style={{
                            height: 8,
                            backgroundColor: '#1c1f24',
                            cursor: 'ns-resize',
                            position: 'relative',
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        onMouseDown={this.handleMouseDown}
                    >
                        <div
                            style={{
                                width: 34,
                                height: 3,
                                background: '#4a515c',
                                borderRadius: 2
                            }}
                        />
                    </div>

                    {/* 콘솔 머리말 — 검은 상자만 있으면 뭘 보는 곳인지 모른다. 로그 지우기도 여기로 모음 */}
                    <div className={pcgStyles.pcgConsoleHead}>
                        <span style={{flex: 1}}>{f(messages.output)}</span>
                        <button
                            onClick={this.clearLogConsole}
                            style={{
                                border: '1px solid #4a515c', background: '#2a2f37', color: '#d7dce3', borderRadius: 6,
                                fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: '3px 10px'
                            }}
                            title={f(messages.clearOutputTitle)}
                        >{f(messages.clearOutput)}</button>
                    </div>
                    <div
                        id="consoleContainer"
                        ref={this.consoleContainerRef}  // 이 줄 추가!
                        style={{
                            display: 'flex',
                            height: consoleHeight, /* flex: 1 → height: consoleHeight */
                            minHeight: 50
                        }}
                    >
                        <div id="consoleBox" style={{flex: 1, overflow: 'hidden'}}></div>
                    </div>
                </div>
            </Box>
        );
    }
}

PythoncodeTab.propTypes = {
    // isRequired 로 둔다 — 이게 빠져 있어서 2026-07-24 에 탭 이동이 조용히 죽은 적 있다
    // (prop 이름 오타 → undefined 호출 → 경고도 예외도 없음). 번역이 안 붙으면 여기서 잡힌다.
    intl: intlShape.isRequired,
    vm: PropTypes.instanceOf(VM).isRequired,
    isRunPythoncode: PropTypes.bool,
    tmpCodeText: PropTypes.string,
    tmpLogText: PropTypes.string,
};

const mapStateToProps = state => ({
    isRunPythoncode: state.scratchGui.pythoncode.isRunPythoncode,
    tmpCodeText: state.scratchGui.pythoncode.tmpCodeText,
    tmpLogText: state.scratchGui.pythoncode.tmpLogText,
});

const mapDispatchToProps = dispatch => ({
    dispatchSetIsRunPythoncode: (isRun) => {
        dispatch(setIsRunPythoncode(isRun));
    },
    dispatchUpdateTmpCodeText: (tmpCodeText) => {
        dispatch(updateTmpCodeText(tmpCodeText));
    },
    dispatchUpdateTmpLogText: (tmpLogText) => {
        dispatch(updateTmpLogText(tmpLogText));
    },
});

export default errorBoundaryHOC('Python code Tab')(
    injectIntl(connect(
        mapStateToProps,
        mapDispatchToProps
    )(PythoncodeTab))
);
// export default PythoncodeTab;
